import {
  EyeIcon,
  EyeOffIcon,
  IconButton,
  Scrollbar,
  Spinner,
  Text,
  defaultTextVariants,
} from "@carbon6/c6-frontend-design";
import { useEffect, useRef, type ReactNode } from "react";
import type { EpisodeSummary } from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { RefreshIcon } from "@/components/icons";
import { DisabledReasonTooltip, HatTextTooltip } from "@/components/tooltip";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { inlineSpinnerClassName, LoadingState } from "@/shared/ui/loadingState/public";
import { absentAxisTip, axisDeviationTip } from "./diagnostics/diagnosticsTerms";
import { episodeBandView, type EpisodeBandAxisView } from "./episodeBand";
import { episodeReviewRecord, episodeReviewRecordTip } from "./episodeReviewRecord";
import {
  EPISODE_QUALITY_FLAG_LABEL,
  episodeQualityFlags,
  type EpisodeQualityFlag,
} from "./episodeReviewState";
import type { LoadState } from "./loadState";
import styles from "./EpisodeList.module.css";

// 다음 페이지를 요청하기 시작하는 거리. 목록 끝의 trigger 가 스크롤 뷰포트 아래
// 한 화면(root 높이의 100%) 안에 들어오면 다음 페이지를 미리 받는다.
const PREFETCH_ROOT_MARGIN = "0px 0px 100% 0px";

const HIDDEN_ONLY_MESSAGE = `Training에서 제외된 에피소드만 있습니다. "숨김 포함 보기"를 눌러 확인해 주세요.`;

const FLAG_BADGE_CLASS: Record<EpisodeQualityFlag, string> = {
  failure: styles.badgeFailure,
  issue: styles.badgeIssue,
  excluded: styles.badgeExcluded,
};

export type EpisodeListProps = {
  // 적재 상태. 로딩/에러 메시지를 고를 때만 쓴다. 그릴 행은 `items` 가 소유한다.
  state: LoadState<EpisodeSummary[]>;
  // override 와 숨김 필터를 적용한 표시 목록. 상위가 `visibleEpisodes` 로 만든다.
  items: readonly EpisodeSummary[];
  // 필터 전 누적 로드 수. "N / total" 표기와 "숨김만 남음" 판정에 쓴다.
  loadedCount: number;
  total: number;
  hasMore: boolean;
  loadingMore: boolean;
  // 숨김(제외) 축. 이 축이 없는 소비처는 둘 다 생략한다. 그러면 눈 아이콘 토글도 그리지 않는다.
  // 눌러도 동작하지 않는 컨트롤을 남기지 않기 위해서다.
  includeExcluded?: boolean;
  onIncludeExcludedChange?: (includeExcluded: boolean) => void;
  // 조건(표시 상태·무효 사유)이 숨김을 끄고 있으면 true 다. 그동안 토글은 보는 중으로 표시하고 끈다.
  // 눌러도 목록이 바뀌지 않기 때문이다. 판정은 `excludedShownByFilters` 가 소유한다.
  includeExcludedForced?: boolean;
  // 서버 리뷰 필터 줄. 있으면 헤더 아래에 렌더한다. Files 트리처럼 필터가 없는 소비처는 생략한다.
  filters?: ReactNode;
  // 품질 플래그가 없을 때 행 오른쪽에 표시할 배지. 리뷰 축이 없는 소비처가 자기 축의 상태를
  // 같은 자리에 표시한다. 어노테이션 스튜디오의 "캡처 N" 이 그 예다.
  // null 을 반환하면 아무것도 그리지 않는다. 품질 플래그가 있으면 품질 플래그를 표시한다.
  renderBadge?: (episode: EpisodeSummary) => ReactNode;
  selectedUuid: string | null;
  // 방금 리뷰를 저장한 행. 잠깐 밝게 표시한 뒤 되돌린다. 이 표시는 toast 대신 쓰는 저장 확인이다.
  // 그래서 상태가 아니라 순간 신호다(값은 곧 다시 null 이 된다).
  // "제외"로 저장한 행은 목록에서 빠지므로 표시할 행이 없다. 행이 사라지는 것이 더 분명한
  // 확인이므로 그대로 둔다.
  flashUuid?: string | null;
  onSelect: (episode: EpisodeSummary) => void;
  onLoadMore: () => void;
  // 조회 실패에서 목록을 다시 받는다.
  onRetry: () => void;
};

function episodeTitle(episode: EpisodeSummary): string {
  return episode.display_name ?? `episode_${episode.episode_index}`;
}

// 아이콘만 표시하므로, 현재 상태와 누르면 일어나는 일을 툴팁이 설명한다.
// 조건이 숨김을 끄고 있을 때의 토글 이유다.
const FORCED_VISIBILITY_TEXT =
  "표시 상태나 무효 사유로 거르는 동안에는 Training에서 제외된 에피소드도 보입니다.";

function visibilityTooltip(includeExcluded: boolean): string {
  return includeExcluded
    ? "Training에서 제외된 에피소드까지 보고 있습니다. 눌러서 감춥니다."
    : "Training에서 제외된 에피소드를 감추고 있습니다. 눌러서 함께 봅니다.";
}

// 에피소드 목록. 프리뷰 선택, 무한 스크롤 트리거, 선택 스크롤 동기화, 리뷰 상태 표시를 한다.
// 데이터 적재, 숨김 필터, 키보드 이동은 소비처(FileBrowser·EpisodesViewer)가 소유한다.
export function EpisodeList({
  state,
  items,
  loadedCount,
  total,
  hasMore,
  loadingMore,
  includeExcluded = true,
  onIncludeExcludedChange,
  includeExcludedForced = false,
  filters,
  renderBadge,
  selectedUuid,
  flashUuid = null,
  onSelect,
  onLoadMore,
  onRetry,
}: EpisodeListProps) {
  const itemRefs = useRef(new Map<string, HTMLButtonElement>());
  const scrollerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  function registerItem(key: string, element: HTMLButtonElement | null) {
    if (element) {
      itemRefs.current.set(key, element);
      return;
    }
    itemRefs.current.delete(key);
  }

  // 선택이 바뀌면 그 항목이 보이도록 스크롤 컨테이너를 스크롤한다(키보드 이동 시 화면 동기화).
  useEffect(() => {
    const element = selectedUuid ? itemRefs.current.get(selectedUuid) : null;
    if (element) scrollItemIntoView(element, scrollerRef.current);
  }, [selectedUuid]);

  // 목록 **끝**의 보이지 않는 trigger 가 스크롤 뷰포트에 가까워지면 다음 페이지를 요청한다.
  // trigger 를 비율 위치(길이의 80% 지점)에 두면, 누적할수록 그 지점이 바닥에서 멀어진다.
  // 남은 뒷부분이 뷰포트보다 길어지면 끝까지 스크롤해도 trigger 가 화면 위에 남아 다시 교차하지 않는다.
  // 그래서 trigger 를 바닥에 고정하고, 미리 받는 거리는 rootMargin 이 정한다.
  // MultiSelectList 도 같은 방식을 쓴다.
  // items 가 바뀌면 다시 observe 한다. trigger 가 계속 교차 상태이면 교차 이벤트가 새로 오지 않는다.
  // 한 페이지로 화면이 차지 않는 경우 이 재관측이 다음 페이지 요청을 계속한다.
  useEffect(() => {
    if (!hasMore) return;
    const trigger = triggerRef.current;
    const root = scrollerRef.current;
    if (!trigger || !root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadMore();
      },
      { root, rootMargin: PREFETCH_ROOT_MARGIN },
    );
    observer.observe(trigger);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore, items]);

  return (
    <div className={styles.listView}>
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <Text variant={hatcheryTextModes["caption-bold"]}>에피소드</Text>
          {state.status === "success" ? (
            <Text variant={hatcheryTextModes["caption-bold"]} className={styles.count}>
              {total > loadedCount ? `${loadedCount} / ${total}` : loadedCount}
            </Text>
          ) : null}
          <div className={styles.headerActions}>
            {/* 숨김 포함 보기. 눈 아이콘 토글로 표시한다. 눌린 상태는 "제외된 것도 보는 중"을
                뜻하므로 라벨이 없어도 읽을 수 있다. 앞뒤 이동 버튼은 두지 않는다. 에피소드가
                수천 개이면 개수 표기에 공간이 모두 필요하다. 이동은 ↑↓ 키로 한다. */}
            {state.status === "success" && onIncludeExcludedChange !== undefined ? (
              includeExcludedForced ? (
                <DisabledReasonTooltip text={FORCED_VISIBILITY_TEXT}>
                  <IconButton
                    aria-label={FORCED_VISIBILITY_TEXT}
                    size="sm"
                    variant="surface"
                    selected
                    disabled
                  >
                    <EyeIcon />
                  </IconButton>
                </DisabledReasonTooltip>
              ) : (
                <HatTextTooltip text={visibilityTooltip(includeExcluded)} placement="bottom">
                  <IconButton
                    aria-label={visibilityTooltip(includeExcluded)}
                    size="sm"
                    variant="surface"
                    selected={includeExcluded}
                    onClick={() => onIncludeExcludedChange(!includeExcluded)}
                  >
                    {includeExcluded ? <EyeIcon /> : <EyeOffIcon />}
                  </IconButton>
                </HatTextTooltip>
              )
            ) : null}
          </div>
        </div>
        {filters}
      </div>
      <Scrollbar ref={scrollerRef} className={styles.scroller} gutter="auto" aria-label="에피소드">
        {state.status === "loading" || state.status === "idle" ? (
          <LoadingState>에피소드를 불러오고 있습니다.</LoadingState>
        ) : null}
        {state.status === "error" ? (
          <EmptyState
            tone="error"
            action={{ label: "다시 시도", onClick: onRetry, icon: <RefreshIcon /> }}
          >
            {state.message}
          </EmptyState>
        ) : null}
        {state.status === "success" && loadedCount === 0 ? (
          <EmptyState>등록된 에피소드가 없습니다.</EmptyState>
        ) : null}
        {state.status === "success" && loadedCount > 0 && items.length === 0 ? (
          <EmptyState>{HIDDEN_ONLY_MESSAGE}</EmptyState>
        ) : null}
        {state.status === "success"
          ? items.map((episode) => (
              <EpisodeListItem
                key={episode.uuid}
                episode={episode}
                selected={episode.uuid === selectedUuid}
                flashing={episode.uuid === flashUuid}
                badge={renderBadge?.(episode) ?? null}
                itemRef={(element) => registerItem(episode.uuid, element)}
                onSelect={() => onSelect(episode)}
              />
            ))
          : null}
        {state.status === "success" && hasMore ? (
          <div ref={triggerRef} className={styles.loadTrigger} aria-hidden="true" />
        ) : null}
        {state.status === "success" && loadingMore ? (
          <div className={styles.sentinel} aria-hidden="true">
            <Spinner size="sm" className={inlineSpinnerClassName} />
            추가 에피소드를 불러오고 있습니다.
          </div>
        ) : null}
      </Scrollbar>
    </div>
  );
}

function EpisodeListItem({
  episode,
  selected,
  flashing,
  badge,
  itemRef,
  onSelect,
}: {
  episode: EpisodeSummary;
  selected: boolean;
  flashing: boolean;
  badge: ReactNode;
  itemRef: (element: HTMLButtonElement | null) => void;
  onSelect: () => void;
}) {
  const flags = episodeQualityFlags(episode);
  return (
    <button
      ref={itemRef}
      type="button"
      className={styles.item}
      data-selected={selected ? "true" : undefined}
      data-excluded={flags.includes("excluded") ? "true" : undefined}
      data-saved-flash={flashing ? "true" : undefined}
      aria-current={selected ? "true" : undefined}
      onClick={onSelect}
    >
      <div className={styles.itemBody}>
        <span className={styles.title} style={defaultTextVariants["body-bold"].style}>
          {`#${episode.episode_index}`} - {episodeTitle(episode)}
        </span>
        {/* 판정, 기록, 밴드를 한 묶음으로 둔다. 좁은 열에서 공간이 부족하면 이름을 글자 몇 개로
            줄이지 않고, 이 묶음 전체를 아랫줄로 옮긴다. */}
        <span className={styles.markers}>
          {flags.length > 0 ? (
            <span className={styles.stateBadges}>
              {flags.map((flag) => (
                <span key={flag} className={`${styles.stateBadge} ${FLAG_BADGE_CLASS[flag]}`}>
                  {flag === "issue"
                    ? `${EPISODE_QUALITY_FLAG_LABEL.issue} ${episode.quality_tags.length}`
                    : EPISODE_QUALITY_FLAG_LABEL[flag]}
                </span>
              ))}
            </span>
          ) : (
            badge
          )}
          <ReviewRecordBadge episode={episode} />
          {episode.accepted_invalid ? (
            <HatTextTooltip text="결함을 수용한 에피소드입니다. 자동 판정과 소견은 유지됩니다.">
              <span className={styles.recordBadge}>수용</span>
            </HatTextTooltip>
          ) : null}
          <BandAxes episode={episode} />
        </span>
      </div>
    </button>
  );
}

/**
 * 적어 둔 기록의 양. 판정 배지가 표시하지 않는 축(표기·코멘트)만 센다.
 *
 * 셀 것이 없으면 **아무것도 그리지 않는다**(공간도 차지하지 않는다).
 * 미검토를 세거나 색칠하지 않는다는 결정을 지키기 위해서다. 기록 내용은 툴팁이 설명한다.
 */
function ReviewRecordBadge({ episode }: { episode: EpisodeSummary }) {
  const record = episodeReviewRecord(episode);
  if (record.total === 0) {
    return null;
  }
  return (
    <HatTextTooltip text={episodeReviewRecordTip(record)}>
      <span className={styles.recordBadge}>{record.total}</span>
    </HatTextTooltip>
  );
}

/**
 * 밴드 이탈 2칸. 위가 Action, 아래가 Observation 이다.
 *
 * **칸은 항상 둘 다 그린다.** 잴 수 없는 축을 지우면 행마다 칸 수가 달라져 목록 정렬이 어긋난다.
 * 또 "없음"과 "0%"가 시각적으로 구분되지 않는다.
 * 잴 수 없는 축은 무채색으로 두고, 툴팁이 이유를 설명한다.
 */
function BandAxes({ episode }: { episode: EpisodeSummary }) {
  const view = episodeBandView(episode);
  return (
    <span className={styles.bandAxes} aria-hidden="true">
      <BandAxisCell axis="action" view={view.action} />
      <BandAxisCell axis="observation" view={view.observation} />
    </span>
  );
}

function BandAxisCell({
  axis,
  view,
}: {
  axis: "action" | "observation";
  view: EpisodeBandAxisView;
}) {
  const tip =
    view.fraction === null
      ? absentAxisTip(axis)
      : axisDeviationTip(axis, view.fraction, view.worstChannel, view.worstFraction);
  return (
    <HatTextTooltip text={tip}>
      <span className={styles.bandCell} data-severity={view.severity ?? undefined} />
    </HatTextTooltip>
  );
}

// 선택 항목이 보이도록 스크롤 컨테이너를 스크롤한다.
//
// offsetTop 은 scroll 컨테이너가 아니라 offsetParent 기준이다. 컨테이너가 positioned 가 아니면
// 좌표계가 달라진다. 그러면 위로 이동할 때 스크롤이 선택을 따라가지 않는다.
// 그래서 뷰포트 기준 rect 차이로 계산한다. 이 계산은 offsetParent 와 DOM 중첩에 영향을 받지 않는다.
function scrollItemIntoView(item: HTMLButtonElement, container: HTMLElement | null) {
  if (!container) return;

  const itemRect = item.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  if (itemRect.top < containerRect.top) {
    container.scrollTop -= containerRect.top - itemRect.top;
  } else if (itemRect.bottom > containerRect.bottom) {
    container.scrollTop += itemRect.bottom - containerRect.bottom;
  }
}
